import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, SidePanel, tailwindOverride } from '@getsynapse/design-system';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { cardTypeSelectors } from 'state/normalized/insights/cardTypeSlice';
import { CardType } from 'utils/customTypes';
import { InsightCategory } from 'types/store/normalized/insights/InsightCatalogue';
import orderBy from 'lodash/orderBy';
import { renderFooterSection } from './components/DashbordSidePanelFooter';
import { renderChartThumbnail } from './components/ChartThumbnail';
import { renderStatusIndicator } from './components/Indicators';

const CATEGORY_ORDER = [
  InsightCategory.REQUESTS,
  InsightCategory.PROJECTS,
  InsightCategory.TASKS,
  InsightCategory.USERS,
  InsightCategory.CAPACITY_MANAGEMENT,
];

const CATEGORY_TRANSLATED_LABELS: Partial<Record<InsightCategory, string>> = {
  [InsightCategory.REQUESTS]: 'ENTITIES.REQUESTS',
  [InsightCategory.PROJECTS]: 'ENTITIES.PROJECTS',
  [InsightCategory.TASKS]: 'ENTITIES.TASKS',
  [InsightCategory.USERS]: 'ENTITIES.USERS',
  [InsightCategory.CAPACITY_MANAGEMENT]: 'ENTITIES.CAPACITY_MANAGEMENT',
};

const getCardClassName = (isExisting: boolean, isSelected: boolean) => {
  let classNames = 'border-2';
  if (isExisting) {
    classNames +=
      ' bg-off-white text-dark-gray cursor-not-allowed border-transparent';
  } else {
    if (isSelected) {
      classNames += ' border-teal-green bg-mint-green';
    } else {
      classNames += ' shadow-skim border-transparent';
    }
    classNames += ' hover:shadow-lifted hover:border-teal-green';
  }
  return classNames;
};

interface DashboardSidePanelProps {
  open: boolean;
  handleClose: () => void;
  cardTypeIdsRenderedOnDashboard: string[];
  handleAddCardTypes: (cardTypes: CardType[]) => void;
}

const CardTypesSidePanel: React.FC<DashboardSidePanelProps> = ({
  open,
  handleClose,
  cardTypeIdsRenderedOnDashboard,
  handleAddCardTypes,
}) => {
  const cardTypes = useSelector(cardTypeSelectors.selectAll);
  const [selectedCardTypes, setSelectedCardTypes] = useState<CardType[]>([]);

  const sortedCardTypesByCategory = useMemo(() => {
    const categoryMap = cardTypes.reduce((acc, cardType) => {
      const cards = acc.get(cardType.category) || [];
      cards.push(cardType);
      acc.set(cardType.category, cards);
      return acc;
    }, new Map<InsightCategory, CardType[]>());

    // Sort each category's cards by name
    categoryMap.forEach((cards, category) => {
      const sortedCards = orderBy(cards, ['name'], ['asc']);
      categoryMap.set(category, sortedCards);
    });

    return categoryMap;
  }, [cardTypes]);

  useEffect(() => {
    setSelectedCardTypes([]);
  }, [open]);

  const toggleCardSelection = useCallback(
    (cardType: CardType) => {
      if (cardTypeIdsRenderedOnDashboard.includes(cardType.id)) {
        return;
      }
      setSelectedCardTypes((prev) => {
        const isAlreadySelected = prev.includes(cardType);
        if (isAlreadySelected) {
          return prev.filter(
            (selectedCardType) => selectedCardType.id !== cardType.id
          );
        } else {
          return [...prev, cardType];
        }
      });
    },
    [cardTypeIdsRenderedOnDashboard]
  );

  const handleAddSelected = useCallback(() => {
    handleAddCardTypes(selectedCardTypes);
    setSelectedCardTypes([]);
    handleClose();
  }, [handleAddCardTypes, handleClose, selectedCardTypes]);

  const renderHeaderSection = useCallback(() => {
    return (
      <div className='sticky top-0 z-10 bg-white'>
        <div className='flex justify-between'>
          <h2 className='text-h4-sm lg:text-h4 font-semibold text-primary mb-4'>
            {intl.get('ADD_INSIGHT.TITLE')}
          </h2>
          <Button
            className='mb-4'
            variant='primary'
            onClick={handleAddSelected}
            disabled={selectedCardTypes.length === 0}
          >
            {`${intl.get('ADD_INSIGHT.ADD_SELECTED')}`}
            {selectedCardTypes.length > 0 && ` (${selectedCardTypes.length})`}
          </Button>
        </div>
      </div>
    );
  }, [handleAddSelected, selectedCardTypes]);

  const renderCardType = useCallback(
    (cardType: CardType, categoryIndex: number) => {
      const isSelected = selectedCardTypes.includes(cardType);
      const isExisting = cardTypeIdsRenderedOnDashboard.includes(cardType.id);

      return (
        <div
          role='button'
          key={cardType.id}
          onClick={() => toggleCardSelection(cardType)}
          className={tailwindOverride(
            'relative group p-2 rounded bg-white',
            getCardClassName(isExisting, isSelected)
          )}
        >
          {renderStatusIndicator(isSelected, isExisting, categoryIndex)}
          {renderChartThumbnail(cardType, isExisting)}
          <h2 className='font-semibold'>{cardType.name}</h2>
          <p className='text-sm text-neutral-darker'>{cardType.description}</p>
        </div>
      );
    },
    [selectedCardTypes, cardTypeIdsRenderedOnDashboard, toggleCardSelection]
  );

  const renderCardTypeCategory = useCallback(
    (category: InsightCategory) => {
      const cardTypes = sortedCardTypesByCategory.get(category) || [];

      const categoryLabel = intl
        .get(CATEGORY_TRANSLATED_LABELS[category] ?? category)
        .defaultMessage(category);

      if (cardTypes.length === 0) return null;

      return (
        <div key={category} className='mb-6'>
          <h3 className='text-lg font-semibold text-primary mb-3'>
            {categoryLabel}
          </h3>
          <div className='grid grid-cols-3 gap-4'>
            {cardTypes.map((cardType, index) =>
              renderCardType(cardType, index)
            )}
          </div>
        </div>
      );
    },
    [sortedCardTypesByCategory, renderCardType]
  );

  const renderSidePanelContent = useCallback(() => {
    return (
      <div className='p-4'>
        {renderHeaderSection()}
        {CATEGORY_ORDER.map((category) => renderCardTypeCategory(category))}
      </div>
    );
  }, [renderCardTypeCategory, renderHeaderSection]);

  const sidePanelContent = useMemo(
    () => (
      <div>
        <SidePanel
          isOpen={open}
          onClose={() => {
            setSelectedCardTypes([]);
            handleClose();
          }}
          footerButtons={[]}
          className='w-content'
        >
          {renderSidePanelContent()}
          {renderFooterSection()}
        </SidePanel>
      </div>
    ),
    [open, handleClose, renderSidePanelContent]
  );

  return sidePanelContent;
};

export default CardTypesSidePanel;
